<template>
  <div class="animated fadeIn">
    <b-row>
      <b-col>
        <hm-spinner v-if="!ready"/>
        <b-form @submit.prevent="onSubmit">
          <b-card header="ePHI Inventory Item" v-show="ready">
            <Loading :active="busy" :is-full-page="false">
              <slot>
                <hm-spinner/>
              </slot>
            </Loading>
            <hm-alert/>
            <b-form-group label="What type of media would you like to add?" label-class="font-weight-bold">
              <b-form-select
                name="itemType"
                class="form-control"
                :options="inventoryMap.Type"
                v-model="item.type"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('itemType') && submitted }"
              >
                <template slot="first">
                  <option :value="''" disabled>-- Please select an option --</option>
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group label="How is the media identified?" label-class="font-weight-bold">
              <b-form-input
                name="itemName"
                type="text"
                class="form-control"
                placeholder="Serial number or unique name (e.g. Reception #1, Laptop #4, Dr. Jeff’s iPhone)"
                v-model="item.name"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('itemName') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Where is the media located day-to-day?" label-class="font-weight-bold">
              <b-form-input
                name="itemLocation"
                type="text"
                class="form-control"
                placeholder="Treatment rm 1, Exam, Billing office, Maintenance closet, Reception desk, etc."
                v-model="item.location"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('itemLocation') && submitted }"
              ></b-form-input>
            </b-form-group>
            <b-form-group label="Is the media located in a public area sometimes or all of the time?" label-class="font-weight-bold">
              <p>A public area might be an unmonitored lobby, room, or hallway, or anywhere away from the business. For example, a device that is taken home would be located in a public area.</p>
              <hm-switch class="mx-1" v-model="item.public" data-type="yesno" :disabled="busy"/>
            </b-form-group>
            <b-form-group label="What ePHI is created, maintained (stored), received, or transmitted using the media?" label-class="font-weight-bold">
              <hm-switch type="radio" class="mx-1 p-1" size="sm" label="None whatsoever" value="" v-model="item.data" data-type="check" :disabled="busy"/>
              <div v-for="(value, key) in inventoryMap.Data" :key="key">
                <hm-switch class="mx-1 p-1" size="sm" :value="key" :label="value" v-model="item.data" data-type="check" :disabled="busy"/>
              </div>
            </b-form-group>
            <b-form-group label="Who is responsible for the use and security of the media?" label-class="font-weight-bold">
              <b-form-select
                name="itemAssignee"
                class="form-control"
                :options="workforceNames"
                v-model="item.assignee"
                v-validate="'required'"
                :disabled="busy"
                :class="{ 'is-invalid': errors.has('itemAssignee') && submitted }"
              >
                <template slot="first">
                  <option :value="''" disabled>-- Please select an option --</option>
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group label="Is the media/device personally owned, and its owner has permission to use it to access practice data?" label-class="font-weight-bold">
              <hm-switch class="mx-1" v-model="item.assigneeOwned" data-type="yesno" :disabled="busy"/>
            </b-form-group>
            <b-form-group label="Does the media move outside the facility?" label-class="font-weight-bold">
              <p>A workforce member takes the media home or to other locations outside the practice.</p>
              <hm-switch class="mx-1" v-model="item.externalUse" data-type="yesno" :disabled="busy"/>
            </b-form-group>
            <b-form-group label="Is the media critical to support decision making about a patient’s treatment?" label-class="font-weight-bold">
              <p>Loss of the media would adversely impact the practice's ability to diagnose or treat patients.</p>
              <hm-switch class="mx-1" v-model="item.critical" data-type="yesno" :disabled="busy"/>
            </b-form-group>
            <b-form-group label="How would you prioritize the media relative to patient care or business needs?" label-class="font-weight-bold">
              <p>For example, when restoring systems following an emergency, a server computer will have more importance to practice operations than a tablet used for patient check in.</p>
              <div v-for="(value, key) in inventoryMap.Priority" :key="key">
                <hm-switch
                  name="itemPriority"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.priority"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemPriority') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Think of the data being accessed using the media/device. Does your practice back up that data?" label-class="font-weight-bold">
              <p>Saving an exact copy to magnetic tape, optical disc (CD), external hard drive, or virtual storage, such as a cloud environment.</p>
              <div v-for="(value, key) in inventoryMap.Backup" :key="key">
                <hm-switch
                  name="itemBackup"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.backup"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemBackup') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Is automatic logoff activated for the media?" label-class="font-weight-bold">
              <p>A session automatically terminates after a predetermined period of user inactivity.</p>
              <div v-for="(value, key) in inventoryMap.Logoff" :key="key">
                <hm-switch
                  name="itemLogoff"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.logoff"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemLogoff') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Look at the area surrounding the media/device. What physical protections are in place to help secure the media/device?" label-class="font-weight-bold">
              <div v-for="(value, key) in inventoryMap.ProtectionsArea" :key="key">
                <hm-switch name="itemProtections" class="mx-1 p-1" size="sm" :value="key" :label="value" v-model="item.protections" data-type="check" :disabled="busy"/>
              </div>
            </b-form-group>
            <b-form-group label="Look at the media/device itself. What physical protections are in place to help secure the media/device?" label-class="font-weight-bold">
              <div class="pt-3 pl-2 font-weight-bold">Privacy screen / filter</div>
              <div v-for="(value, key) in inventoryMap.ProtectionScreen" :key="key">
                <hm-switch
                  name="itemProtectionScreen"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.protectionScreen"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemProtectionScreen') && submitted }"
                />
              </div>
              <div class="pt-3 pl-2 font-weight-bold">Swipe card access</div>
              <div v-for="(value, key) in inventoryMap.ProtectionCard" :key="key">
                <hm-switch
                  name="itemProtectionCard"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.protectionCard"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemProtectionCard') && submitted }"
                />
              </div>
              <div class="pt-3 pl-2 font-weight-bold">Cable lock</div>
              <div v-for="(value, key) in inventoryMap.ProtectionCable" :key="key">
                <hm-switch
                  name="itemProtectionCable"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.protectionCable"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemProtectionCable') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="How are users authenticated to assure that a uniquely identified user is the one claimed?" label-class="font-weight-bold">
              <div v-for="(value, key) in inventoryMap.Auth2" :key="key">
                <hm-switch
                  name="itemAuth"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.auth2"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemAuth') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Is the media protected against malware?" label-class="font-weight-bold">
              <p>Antivirus software is turned on and regularly updated.</p>
              <div v-for="(value, key) in inventoryMap.AV" :key="key">
                <hm-switch
                  name="itemAV"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.av"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemAV') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group>
              <div class="font-weight-bold" slot="label">Is the hard drive of the media encrypted?
                <hm-popover title="Encryption">
                  <p>
                    Encryption prevents unauthorized access to information stored on your computer's hard drive (disk) by converting the data into unreadable code. Only someone (you) with the password and key can access the stored data. After you encrypt your hard drive
                    you'll need to enter your password before you can do anything else.
                  </p>
                  <p>Both Microsoft and Apple have built in encryption features you can turn on, but there are plenty of third-party tools as well.</p>
                  <ul>
                    <li>Microsoft: BitLocker</li>
                    <li>Apple: FileVault</li>
                  </ul>
                  <p>There are tons of tutorials online if you need assistance enabling these features.</p>
                </hm-popover>
              </div>
              <div v-for="(value, key) in inventoryMap.Encrypted" :key="key">
                <hm-switch
                  name="itemEncrypted"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.encrypted"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemEncrypted') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Is transmission encryption utilized to assure ePHI is not compromised when being transmitted from one point to another?" label-class="font-weight-bold">
              <p>This includes transmission within the office or between the practice and another entity.</p>
              <div v-for="(value, key) in inventoryMap.EncryptedTransmission" :key="key">
                <hm-switch
                  name="itemEncryptedTransmission"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.encryptedTransmission"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemEncryptedTransmission') && submitted }"
                />
              </div>
            </b-form-group>
            <b-form-group label="Are mechanisms in place to corroborate ePHI has not been altered, modified or destroyed in an unauthorized manner?" label-class="font-weight-bold">
              <p>
                Consider whether the practice has data authentication mechanisms and tools, such as checksum. Checksum is a computation introduced when ePHI is transmitted or stored. The computation is checked at a later time (such as when ePHI recalled or when it is
                received at the intended destination) to ascertain whether the computations match. If the checksum matches, then the ePHI was not altered or modified.
              </p>
              <div v-for="(value, key) in inventoryMap.Repudiation" :key="key">
                <hm-switch
                  name="itemRepudiation"
                  type="radio"
                  class="mx-1 p-1"
                  size="sm"
                  :value="key"
                  :label="value"
                  v-model="item.repudiation"
                  data-type="check"
                  v-validate="'required'"
                  :disabled="busy"
                  :label-class="{ 'is-invalid': errors.has('itemRepudiation') && submitted }"
                />
              </div>
            </b-form-group>

            <div slot="footer">
              <b-row>
                <b-col cols="6">
                  <b-button type="button" variant="secondary" class="px-4" :disabled="busy" to="/inventory-list">Back</b-button>
                  <b-button type="button" variant="danger" class="px-4 ml-2" :disabled="busy" v-show="id" @click="dlgDisposal = true">Delete</b-button>
                </b-col>
                <b-col cols="6" class="text-right">
                  <b-button type="submit" variant="primary" class="px-4" :disabled="busy">Save</b-button>
                </b-col>
              </b-row>
            </div>
          </b-card>
        </b-form>
      </b-col>
    </b-row>

    <!-- ePHI Disposal Modal Dialog -->
    <b-modal id="dlgDisposal" class="modal-primary" hide-header-close v-model="dlgDisposal">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-user-times pr-2"></i>ePHI
      </div>
      <p>Have you removed or destroyed ePHI from this information technology device and media prior to disposal of the device?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDisposal = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDisposal = false; dlgDelete = true">Yes</b-button>
      </div>
    </b-modal>

    <!-- Delete Modal Dialog -->
    <b-modal id="dlgDelete" class="modal-primary" hide-header-close v-model="dlgDelete">
      <div class="font-weight-bold" slot="modal-title">
        <i class="fa fas fa-trash pr-2"></i>Delete Confirmation
      </div>
      <p>Are you sure you want to delete this item?</p>
      <div slot="modal-footer">
        <b-button variant="default" @click="dlgDelete = false">No</b-button>
        <b-button variant="primary" class="ml-2" @click="dlgDelete = false; onDelete()">Yes</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { inventoryService, workforceService } from '@/services'
import Enumerable from 'linq/linq.js'

export default {
  name: 'InventoryEdit',
  props: ['id'],
  data() {
    return {
      item: {
        id: '',
        type: '',
        name: '',
        location: '',
        public: false,
        data: [],
        assignee: '',
        assigneeOwned: false,
        critical: false,
        externalUse: false,
        priority: '',
        backup: '',
        logoff: '',
        protections: [],
        auth: [],
        av: '',
        encrypted: '',
        encryptedTransmission: '',
        repudiation: ''
      },
      workforce: [],
      workforceNames: [],
      dlgDisposal: false,
      dlgDelete: false,
      ready: false,
      busy: false,
      submitted: false
    }
  },
  computed: {
    ...mapState('maps', ['inventoryMap', 'workforceMap'])
  },
  methods: {
    ...mapActions({
      alertClear: 'alert/clear',
      alertError: 'alert/error',
      alertException: 'alert/exception',
      setProgress: 'progress/setFlagDone'
    }),
    onSubmit() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      this.$validator.validate()
        .then(valid => {
          if (valid) {
            return inventoryService.setInventory(this.item)
              .then(data => {
                this.setProgress('InventorySet');
                this.$router.push('/inventory-list');
              })
              .catch(err => {
                this.alertException({ err, message: 'Error saving inventory item.' });
              });
          } else {
            this.alertError('Please complete inventory item information and submit again.');
          }
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onDelete() {
      this.busy = true;
      this.submitted = true;
      this.alertClear();

      return inventoryService.deleteInventory(this.item)
        .then(data => {
          this.$router.push('/inventory-list');
        })
        .catch(err => {
          this.alertException({ err, message: 'Error deleting inventory item.' });
        })
        .finally(() => {
          this.busy = false;
        });
    },
    onLoad() {
      this.ready = false;

      Promise.all([
        this.$store.dispatch('maps/fetchInventoryMap'),
        workforceService.getWorkforce()
      ]).then((values) => {
        this.workforce = values[1];
        this.workforceNames = Enumerable.from(this.workforce).where(x => { return x.active; }).select(x => { return x.name; }).toArray();

        if (this.id) {
          return inventoryService.getInventory(this.id)
            .then(data => {
              // set any defaults that are missing from data to the default values
              this.item = { ...this.$options.data.apply(this).item, ...data };
            })
            .catch(err => {
              this.alertException({ err, message: 'Error loading inventory item.' });
            });
        } else {
          return this.item;
        }
      }).catch((err) => {
        this.alertException({ err, message: 'Error loading data.' });
      }).finally(() => {
        this.ready = true;
      })
    }
  },
  created() {
    return this.onLoad();
  },
  watch: { // you have to use function syntax here, as warned in the docs
    '$route.params.id': function (newVal, oldVal) {
      this.onLoad();
    }
  }
}
</script>